import React, { useState, useCallback, useMemo, useEffect } from 'react';
import Dropdown from '../../Assets/Dropdown';
import { nflTeams, isHigherBetter } from '../../../utils/nflUtils';
import { UpCaret, DownCaret } from '../../common/icons';

const NflStatAvgs = (props) => {
    const game = props.game;
    const home_team = nflTeams[game.home.team_id];
    const away_team = nflTeams[game.away.team_id];
    const [activeTab, setActiveTab] = useState('genOff');
    const showCarets = props.showCarets !== false; // Default to true unless explicitly set to false
    
    // Define color constants
    const BETTER_COLOR = "#43a047"; // Green
    const WORSE_COLOR = "#e53935"; // Red

    const genOffLabels = {
        "offensive_plays": "Offensive Plays",
        "first_downs":"First Downs",
        "total_yds":"Total Yards",
        "fumbles":"Fumbles",
        "fumbles_lost": "Fumbles Lost",
        "turnovers":"Turnovers",
        "possession":"Possession (Mins)"
    };
    const rushOffLabels = {
        "att":"Attempts",
        "yds":"Yards",
        "tds":"Touchdowns",
        "long":"Long",
        "ybc":"Yards before contact",
        "yac":"Yards after contact",
        "broken_tackles":"Broken tackles",
        "first_downs":"First Downs"
    };
    const passOffLabels = {
        "att":"Attempts",
        "comp":"Completions",
        "yds":"Yards",
        "tds":"Touchdowns",
        "ints":"Interceptions",
        "net_yds":"Net yards",
        "pass_rating":"Pass rating",
        "long":"Long",
        "first_downs":"First Downs",
        "intended_air_yds":"Intended air yards",
        "comp_air_yds":"Completed air yards",
        "yac":"Yards after catch",
        "poor_throws":"Poor throws",
        "blitzed":"Times blitzed",
        "hurried":"QB hurried",
        "hits_taken":"QB hits taken",
        "pressured":"QB pressured",
        "scrambles":"Scrambles",
        "scramble_yds":"Scramble yards",
        "dropbacks":"Dropbacks",
        "spikes_throwaways":"Spikes / Throwaways",
        "taken": "Sacks taken",
        "yds_lost": "Sack yards lost"
    };

    const passDefLabels = {
        "ints":"Interceptions",
        "int_yds":"Interception yards",
        "int_tds":"Pick 6s",
        "int_long":"Longest return",
        "passes_defended":"Passes defended",
    }
    const tackDefLabels = {
        "sacks":"Sacks",
        "solo":"Solo tackles",
        "assists":"Assisted tackles",
        "for_loss":"Tackles for loss",
        "qb_hits": "QB hits",
        "missed_tackles":"Missed tackles"
    };

    const genDefLabels = {
        "qb_hits":"QB hits",
        "recovered":"Fumbles recovered",
        "forced":"Fumbles forced",
        "tds":"Fumble touchdown returns",
        "yds_ret":"Fumble yards returned",
        "player_blitzes":"Players Blitzed",
        "player_hurries":"QB hurries",
        "qb_knockdowns":"QB knockdowns",
        "player_pressures":"QB pressures",
        "safeties": "Safeties",
        "takeaways": "Takeaways"
    };

    // Memoize stats calculations
    const [genOffStats, setGenOffStats] = useState(null);
    const [rushOffStats, setRushOffStats] = useState(null);
    const [passOffStats, setPassOffStats] = useState(null);
    const [passDefStats, setPassDefStats] = useState(null);
    const [tackDefStats, setTackDefStats] = useState(null);
    const [genDefStats, setGenDefStats] = useState(null);

    useEffect(()=>{
        if('off' in game.home && 'off' in game.away){
            const genOffKeys = Object.keys(game.home.off)
                .filter(key => typeof game.home.off[key] !== 'object')
                .map(key => key);
            const rushOffKeys = Object.keys(game.home.off.rushing);
            let passOffKeys = Object.keys(game.home.off.passing);
            let sackOffKeys = Object.keys(game.home.off.sacks);
            let tmpGenOffStats = {}, tmpRushOffStats = {}, tmpPassOffStats = {}, tmpPassDefStats = {}, tmpTackDefStats = {}, tmpGenDefStats = {}
            const passDefKeys = Object.keys(game.home.def.passing);
            const tackDefKeys = Object.keys(game.home.def.tackles);
            const fumbDefKeys = Object.keys(game.home.def.fumbles);
            const genDefKeys = Object.keys(game.home.def)
                .filter(key => typeof game.home.def[key] !== 'object')
                .map(key => key);

            // get general offensive stats
            genOffKeys.forEach(stat=>{
                tmpGenOffStats[stat] = [game.home.off[stat], game.away.off[stat]]
            });
            // get offensive passing stats
            passOffKeys.forEach(stat=>{
                tmpPassOffStats[stat] = [game.home.off.passing[stat], game.away.off.passing[stat]]
            });
            sackOffKeys.forEach(stat=>{
                tmpPassOffStats[stat] = [game.home.off.sacks[stat], game.away.off.sacks[stat]]
            });
            // get offensive rushing stats 
            rushOffKeys.forEach(stat=>{
                tmpRushOffStats[stat] = [game.home.off.rushing[stat], game.away.off.rushing[stat]]
            });
            // get defensive passing stats
            passDefKeys.forEach(stat=>{
                tmpPassDefStats[stat] = [game.home.def.passing[stat], game.away.def.passing[stat]]
            });
            // get defensive tackling stats
            tackDefKeys.forEach(stat=>{
                tmpTackDefStats[stat] = [game.home.def.tackles[stat], game.away.def.tackles[stat]]
            });
            tmpTackDefStats["missed_tackles"] = [game.home.def.missed_tackles, game.away.def.missed_tackles]
            // get general defensive stats
            fumbDefKeys.forEach(stat=>{
                tmpGenDefStats[stat] = [game.home.def.fumbles[stat], game.away.def.fumbles[stat]]
            });
            genDefKeys.forEach(stat=>{
                if(stat !== "missed_tackles"){
                    tmpGenDefStats[stat] = [game.home.def[stat], game.away.def[stat]]
                };
            });
            // set stats
            setGenOffStats(tmpGenOffStats);
            setRushOffStats(tmpRushOffStats);
            setPassOffStats(tmpPassOffStats);
            setPassDefStats(tmpPassDefStats);
            setTackDefStats(tmpTackDefStats);
            setGenDefStats(tmpGenDefStats);
            console.log(tmpGenDefStats)
        };
    }, [props]);

    // Determine if home or away has better stat
    const compareStats = useCallback((stat, homeVal, awayVal, category) => {
        if (homeVal === awayVal) return null;
        
        let statKey;
        switch(category) {
            case 'genOff':
                statKey = `off.${stat}`;
                break;
            case 'rushOff':
                statKey = `off.rushing.${stat}`;
                break;
            case 'passOff':
                if (stat === "taken" || stat === "yds_lost") {
                    statKey = `off.sacks.${stat}`;
                } else {
                    statKey = `off.passing.${stat}`;
                }
                break;
            case 'passDef':
                statKey = `def.passing.${stat}`;
                break;
            case 'tackDef':
                if (stat === "missed_tackles") {
                    statKey = `def.${stat}`;
                } else if (["recovered", "tds", "yds_ret", "forced"].includes(stat)) {
                    statKey = `def.fumbles.${stat}`;
                } else {
                    statKey = `def.tackles.${stat}`;
                }
                break;
            case 'genDef':
                if (["recovered", "tds", "yds_ret", "forced"].includes(stat)) {
                    statKey = `def.fumbles.${stat}`;
                } else {
                    statKey = `def.${stat}`;
                }
                break;
            default:
                return null;
        }
        
        const higherBetter = isHigherBetter[statKey];
        
        if (higherBetter) {
            return homeVal > awayVal ? 'home' : 'away';
        } else {
            return homeVal < awayVal ? 'home' : 'away';
        }
    }, []);

    // Memoized render functions with updated styling
    const renderTableRow = useCallback((stat, homeValue, awayValue, labels, category, statPath = '', idx, total) => {
        const better = showCarets ? compareStats(stat, homeValue, awayValue, category) : null;
        const isHigherBet = isHigherBetter[statPath || `${category}.${stat}`];
        const isLast = idx === total - 1;
        
        return (
            <tr key={stat}>
                <td className={`${!isLast ? 'border-b border-gray-200 dark:border-gray-700' : ''} truncate py-4 pl-6 pr-3 text-sm font-medium text-gray-900 dark:text-gray-100 sm:pl-8 lg:pl-10`}>
                    {labels[stat]}
                </td>
                <td className={`${!isLast ? 'border-b border-gray-200 dark:border-gray-700' : ''} truncate px-3 py-4 text-sm text-gray-900 dark:text-gray-100 ${better === 'home' ? 'font-medium text-green-700 dark:text-green-500' : better === 'away' ? 'font-medium text-red-700 dark:text-red-500' : ''}`}>
                    {homeValue}
                    {better === 'home' && (
                        isHigherBet ? (
                            <UpCaret className="inline-block ml-1 w-4 h-4" fillColor={BETTER_COLOR} />
                        ) : (
                            <DownCaret className="inline-block ml-1 w-4 h-4" fillColor={BETTER_COLOR} />
                        )
                    )}
                    {better === 'away' && (
                        isHigherBet ? (
                            <DownCaret className="inline-block ml-1 w-4 h-4" fillColor={WORSE_COLOR} />
                        ) : (
                            <UpCaret className="inline-block ml-1 w-4 h-4" fillColor={WORSE_COLOR} />
                        )
                    )}
                </td>
                <td className={`${!isLast ? 'border-b border-gray-200 dark:border-gray-700' : ''} truncate px-3 py-4 text-sm text-gray-900 dark:text-gray-100 ${better === 'away' ? 'font-medium text-green-700 dark:text-green-500' : better === 'home' ? 'font-medium text-red-700 dark:text-red-500' : ''}`}>
                    {awayValue}
                    {better === 'away' && (
                        isHigherBet ? (
                            <UpCaret className="inline-block ml-1 w-4 h-4" fillColor={BETTER_COLOR} />
                        ) : (
                            <DownCaret className="inline-block ml-1 w-4 h-4" fillColor={BETTER_COLOR} />
                        )
                    )}
                    {better === 'home' && (
                        isHigherBet ? (
                            <DownCaret className="inline-block ml-1 w-4 h-4" fillColor={WORSE_COLOR} />
                        ) : (
                            <UpCaret className="inline-block ml-1 w-4 h-4" fillColor={WORSE_COLOR} />
                        )
                    )}
                </td>
            </tr>
        );
    }, [showCarets, compareStats, BETTER_COLOR, WORSE_COLOR]);

    // Updated memoized renders using the common renderTableRow function
    const renderGenOffRows = useMemo(() => {
        if (!genOffStats) return null;
        const stats = Object.keys(genOffStats);
        return stats.map((stat, idx) => 
            renderTableRow(stat, game.home.off[stat], game.away.off[stat], genOffLabels, 'genOff', '', idx, stats.length)
        );
    }, [genOffStats, game, renderTableRow]);

    const renderRushOffRows = useMemo(() => {
        if (!rushOffStats) return null;
        const stats = Object.keys(rushOffStats);
        return stats.map((stat, idx) => 
            renderTableRow(stat, game.home.off.rushing[stat], game.away.off.rushing[stat], rushOffLabels, 'rushOff', 'off.rushing.' + stat, idx, stats.length)
        );
    }, [rushOffStats, game, renderTableRow]);

    const renderPassOffRows = useMemo(() => {
        if (!passOffStats) return null;
        const stats = Object.keys(passOffStats);
        return stats.map((stat, idx) => {
            const homeValue = stat !== "taken" && stat !== "yds_lost" 
                ? game.home.off.passing[stat] 
                : game.home.off.sacks[stat];
            const awayValue = stat !== "taken" && stat !== "yds_lost" 
                ? game.away.off.passing[stat] 
                : game.away.off.sacks[stat];
            const statPath = stat === "taken" || stat === "yds_lost" 
                ? `off.sacks.${stat}` 
                : `off.passing.${stat}`;
            return renderTableRow(stat, homeValue, awayValue, passOffLabels, 'passOff', statPath, idx, stats.length);
        });
    }, [passOffStats, game, renderTableRow]);

    const renderPassDefRows = useMemo(() => {
        if (!passDefStats) return null;
        const stats = Object.keys(passDefStats);
        return stats.map((stat, idx) => 
            renderTableRow(stat, game.home.def.passing[stat], game.away.def.passing[stat], passDefLabels, 'passDef', 'def.passing.' + stat, idx, stats.length)
        );
    }, [passDefStats, game, renderTableRow]);

    const renderTackDefRows = useMemo(() => {
        if (!tackDefStats) return null;
        const stats = Object.keys(tackDefStats);
        return stats.map((stat, idx) => {
            let homeValue, awayValue, statPath;
            if (stat === "missed_tackles") {
                homeValue = game.home.def[stat];
                awayValue = game.away.def[stat];
                statPath = `def.${stat}`;
            } else {
                homeValue = game.home.def.tackles[stat];
                awayValue = game.away.def.tackles[stat];
                statPath = `def.tackles.${stat}`;
            }
            return renderTableRow(stat, homeValue, awayValue, tackDefLabels, 'tackDef', statPath, idx, stats.length);
        });
    }, [tackDefStats, game, renderTableRow]);

    const renderGenDefRows = useMemo(() => {
        if (!genDefStats) return null;
        const stats = Object.keys(genDefStats);
        return stats.map((stat, idx) => {
            let homeValue, awayValue, statPath;
            if (["recovered", "tds", "yds_ret", "forced"].includes(stat)) {
                homeValue = game.home.def.fumbles[stat];
                awayValue = game.away.def.fumbles[stat];
                statPath = `def.fumbles.${stat}`;
            } else {
                homeValue = game.home.def[stat];
                awayValue = game.away.def[stat];
                statPath = `def.${stat}`;
            }
            return renderTableRow(stat, homeValue, awayValue, genDefLabels, 'genDef', statPath, idx, stats.length);
        });
    }, [genDefStats, game, renderTableRow]);

    const renderTable = (rows) => {
        if (!rows) return null;
        
        return (
            <div className="mt-8 flow-root">
                <div className="-mx-4 -my-2 sm:-mx-6 lg:-mx-8">
                    <div className="inline-block w-full py-2 align-middle">
                        <div className="max-h-[600px] overflow-y-auto shadow ring-1 ring-black ring-opacity-5 rounded-lg dark:ring-gray-700">
                            <table className="min-w-full w-full table-fixed border-separate border-spacing-0">
                                <thead>
                                    <tr>
                                        <th scope="col" className="w-[50%] sticky top-0 z-10 border-b border-gray-300 bg-white/75 py-3.5 pl-6 pr-3 text-left text-sm font-semibold text-gray-900 backdrop-blur-sm backdrop-filter sm:pl-8 lg:pl-10 dark:bg-gray-800/75 dark:border-gray-700 dark:text-gray-100">Stat</th>
                                        <th scope="col" className="w-[25%] sticky top-0 z-10 border-b border-gray-300 bg-white/75 px-4 py-3.5 text-left text-sm font-semibold text-gray-900 backdrop-blur-sm backdrop-filter dark:bg-gray-800/75 dark:border-gray-700 dark:text-gray-100">{home_team}</th>
                                        <th scope="col" className="w-[25%] sticky top-0 z-10 border-b border-gray-300 bg-white/75 px-4 py-3.5 text-left text-sm font-semibold text-gray-900 backdrop-blur-sm backdrop-filter dark:bg-gray-800/75 dark:border-gray-700 dark:text-gray-100">{away_team}</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                                    {rows}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>
        );
    };
    
    return (
        <div className="sm:max-w-[600px] md:max-w-[700px] lg:max-w-[800px] px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between mb-4 relative z-20 mt-1">
                <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">Stat Averages</h3>
                <Dropdown
                    id="stat-type-select"
                    title="Select Stats Type"
                    items={[
                        { value: 'genOff', label: 'General Offense' },
                        { value: 'rushOff', label: 'Offensive Rushing' },
                        { value: 'passOff', label: 'Offensive Passing' },
                        { value: 'genDef', label: 'General Defense' },
                        { value: 'tackDef', label: 'Defensive Tackling' },
                        { value: 'passDef', label: 'Defensive Passing' }
                    ]}
                    activeItem={activeTab}
                    onSelect={setActiveTab}
                    size="md"
                    variant="default"
                />
            </div>
            
            {activeTab === "genOff" && renderTable(renderGenOffRows)}
            {activeTab === "passOff" && renderTable(renderPassOffRows)}
            {activeTab === "rushOff" && renderTable(renderRushOffRows)}
            {activeTab === "tackDef" && renderTable(renderTackDefRows)}
            {activeTab === "passDef" && renderTable(renderPassDefRows)}
            {activeTab === "genDef" && renderTable(renderGenDefRows)}
        </div>
    );
};

export default NflStatAvgs;